import React, { useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import * as datefns from 'date-fns'
import useAuth from './useAuth'
import { EntityType } from '../types/EntityType'
import { atom, useAtom } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import { EnrichedIssueEntity, IssueEntity } from '../types/IssueEntity'
import CacheApi from '../api/CacheApi'
import CompanyApi from '../api/CompanyApi'
import IssueApi from '../api/IssueApi'
import WarrantApi from '../api/WarrantApi'
import NewsApi from '../api/NewsApi'
import { useInterval } from './useInterval'
import { CompanyEntity } from 'types/CompanyEntity'
import { findSubscriptionPeriodCloseToDate } from '../utils/warrants'
import { getPreviousSubscriptionPeriods } from '../pages/warrants/WarrantOutcome'
import ConvertiblesApi from '../api/ConvertiblesApi'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { hasSourceOfType } from '../components/utils/calculateIssueStatus'
import { NewsListItem, WarrantListItem } from '@finance-lab/utils/types'

type CacheResult<T = unknown> = {
    isFetching: boolean
    isLoading: boolean
    result: T[] | null
    latestUpdate: string | null
}

type CacheStore = {
    [key in EntityType]: CacheResult<EntityCacheType<key>>
}

const POLL_INTERVAL_SECONDS = import.meta.env.VITE_CACHE_POLL_INTERVAL_SECONDS
    ? +import.meta.env.VITE_CACHE_POLL_INTERVAL_SECONDS
    : 60

const entitiesAtom = atom<CacheStore>({})

const entitiesFilter: { [key in EntityType]: (item: unknown[]) => unknown[] } = {
    issue: (issues) =>
        (issues as IssueEntity[]).filter((issue) => !hasSourceOfType(issue, 'Cancellation') && issue?.publishedAt),
    company: (companies) =>
        (companies as { name: string }[]).sort((a, b) => {
            if (a.name && b.name) {
                return a.name.localeCompare(b.name)
            }
            if (a.name && !b.name) {
                return -1
            }
            if (!a.name && b.name) {
                return 1
            }

            return 0
        }),
    news: (news) => {
        let items = (news as NewsListItem[]).filter(
            (item) => item.issueData?.publishedAt || !item.warrantData?.cancelled,
        )

        items = items.map((item) => {
            return {
                tempId: uuidv4(), // add uuid to guarantee unique key
                ...item,
                publicationDate: !item.publicationDate
                    ? null
                    : datefns.format(new Date(item.publicationDate), 'yyyy-MM-dd'),
            }
        })

        items = items.filter(
            (item) =>
                item.eventType !== 'supplement' && item.eventType !== 'update' && item.eventType !== 'stabilisation',
        )

        items = items.filter((item) => {
            return (
                item.eventType !== 'outcome' ||
                item.entityType === 'Rights issue' ||
                item.entityType === 'ABB Directed issue' ||
                item.entityType === 'Warrant' ||
                item.entityType === 'IPO'
            )
        })

        return items.filter((item) => {
            if (item.entityType === 'Warrant') {
                if (item.eventType === 'outcome') {
                    const subscriptionPeriods = getPreviousSubscriptionPeriods(item.warrantData)

                    const sortedSubscriptionPeriods = subscriptionPeriods.sort(
                        (a, b) => new Date(b.subEndDate).getTime() - new Date(a.subEndDate).getTime(),
                    )

                    return (
                        sortedSubscriptionPeriods.length > 0 && sortedSubscriptionPeriods[0].sharesSubscribed !== null
                    )
                } else if (item.eventType === 'terms') {
                    const subscriptionPeriod = findSubscriptionPeriodCloseToDate(
                        { subscriptionPeriods: item.warrantData.subscriptionPeriods },
                        new Date().toISOString(),
                    )

                    return !!subscriptionPeriod && subscriptionPeriod.warrantSubPrice !== null
                } else if (item.eventType === 'publication') {
                    return item.warrantData.origins === null || item.warrantData.origins.length === 0
                } else if (item.eventType === 'new commitments') {
                    return true
                }

                return false
            } else if (item.entityType === 'Convertible') {
                return (
                    (item.eventType === 'publication' && item.convertibleData.loanAmount !== null) ||
                    (item.eventType === 'conversion' && item.convertibleData.conversionAmount !== null)
                )
            }

            const outcomeNews = ['outcome', 'preliminary outcome'].includes(item.eventType)

            return !outcomeNews || (outcomeNews && (item.issueData.unitSubTotal || item.entityType === 'IPO'))
        })
    },
    warrant: (warrants) => warrants.filter((warrant) => !(warrant as { cancelled: boolean })?.cancelled),
    convertible: (convertibles) => convertibles,
}

type EntityCacheType<T extends EntityType> = T extends 'issue'
    ? EnrichedIssueEntity
    : T extends 'warrant'
      ? WarrantListItem
      : T extends 'company'
        ? CompanyEntity
        : T extends 'news'
          ? NewsListItem
          : T extends 'convertible'
            ? ConvertibleEntity
            : never

export default function useFetchEntitiesWithCache<T extends EntityType>(
    entityType: T,
): CacheResult<EntityCacheType<T>> {
    const [entities, setEntities] = useAtom<CacheStore>(entitiesAtom)
    const getEntities = useAtomCallback(useCallback((get) => get(entitiesAtom), [])) // Used to get instantaneous value
    const { isAuthenticated } = useAuth()
    const fetcher = React.useCallback(async () => {
        if (getEntities()[entityType]?.isFetching) return

        setEntities((e) => ({
            ...e,
            [entityType]: {
                ...e[entityType],
                isFetching: true,
                isLoading: !e[entityType], // First fetch only
            },
        }))

        if (entities[entityType]?.result) {
            const cacheApi = new CacheApi()
            const latestUpdate = await cacheApi.latestUpdateForResource(entityType)
            if (!latestUpdate || new Date(entities[entityType].latestUpdate as string) > new Date(latestUpdate)) {
                setEntities((e) => ({
                    ...e,
                    [entityType]: {
                        ...e[entityType],
                        isFetching: false,
                        isLoading: false,
                    },
                }))
                console.log('No new server data updates, not updating client cache')
                return
            }
            console.log('New server data updates, fetching into client cache')
        }

        let result
        const companyApi = new CompanyApi()
        const issueApi = new IssueApi()
        const warrantApi = new WarrantApi()
        const newsApi = new NewsApi()
        const convertiblesApi = new ConvertiblesApi()

        switch (entityType) {
            case 'company':
                result = await companyApi.findAll()
                break
            case 'issue':
                result = await issueApi.findAll()
                break
            case 'warrant':
                result = await warrantApi.findAll()
                break
            case 'news':
                result = await newsApi.findAll()
                break
            case 'convertible':
                result = await convertiblesApi.findAll()
                break
            default:
                console.error('Fetch with cache does not support entityType: ', entityType)
                return null
        }

        result = entitiesFilter[entityType](result)

        setEntities((e) => ({
            ...e,
            [entityType]: {
                isFetching: false,
                isLoading: false,
                result,
                latestUpdate: new Date().toISOString(),
            },
        }))
    }, [entities, entityType, getEntities, setEntities])

    useInterval(fetcher, POLL_INTERVAL_SECONDS * 1000, isAuthenticated && !entities[entityType]?.result)

    return (
        entities[entityType] ||
        ({ result: null, latestUpdate: null, isLoading: true } as CacheResult<EntityCacheType<T>>)
    )
}
